import { LoadingOutlined } from '@ant-design/icons';
import { T, useTranslate } from '@tolgee/react';
import { List } from 'antd';
import { useMemo, useState } from 'react';

import FolderSvg from '../../assets/icons/FolderSvg';
import Play from '../../assets/icons/Play';
import theme from '../../assets/theme';
import useTemplates from '../../hooks/useTemplates';
import { Folder, Template } from '../../services/TemplateService/Template.type';
import { getTitle } from '../../utils/templateUtils';
import { Button } from '../_atoms/Button';
import { IconWrapper } from '../_atoms/IconWrapper';
import BaseModal from '../_molecules/BaseModal';

export const MoveToTrashModal = ({
  deleteData,
  setDeleteData,
  handleDelete,
  isDeletingData,
  folders,
}: {
  deleteData: Template | Folder;
  setDeleteData: (data: Template | Folder | undefined) => void;
  isDeletingData: boolean;
  handleDelete: (data: Template | Folder) => void;
  folders: Folder[];
}) => {
  const { t } = useTranslate();
  const [deletedChildren, setDeletedChildren] = useState<
    (Pick<Template, 'type' | 'title'> | Pick<Folder, 'title' | 'type'>)[] | undefined
  >();
  const templates = useTemplates(deleteData.id).templates;
  useMemo(() => {
    if (deleteData.type === 'folder') {
      const toBeDeletedChildren = folders
        .slice(0, 10)
        .map(f => ({ title: f.title, type: f.type }))
        .concat(
          templates
            ? templates.slice(0, 10).map(t => ({
                title: getTitle({ title: t.title, maxLength: 75 }),
                type: t.type,
              }))
            : [],
        );
      setDeletedChildren(toBeDeletedChildren);
    }
  }, [deleteData, templates, folders]);
  return (
    <>
      <BaseModal
        title={
          deleteData.title
            ? t('Move "{title}" to the trash?', 'Move "{title}" to the trash?', {
                title: deleteData.title,
              })
            : t('Move to the trash?', 'Move to the trash?')
        }
        open
        onCancel={() => setDeleteData(undefined)}
      >
        <p
          style={{
            marginTop: 20,
            marginBottom: 20,
            width: '100%',
            textAlign: 'left',
            fontWeight: 'bold',
          }}
        >
          {deletedChildren && (
            <>
              <p>{`This will move "${deleteData.title}" to the trash along with`}</p>

              <List
                dataSource={deletedChildren}
                renderItem={(
                  item: Pick<Template, 'title' | 'type'> | Pick<Folder, 'title' | 'type'>,
                ) => (
                  <List.Item
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '10px',
                      border: 'none',
                      padding: '10px',
                      marginTop: '5px',
                    }}
                  >
                    <span style={{ fontWeight: 'bold', display: 'flex' }}>
                      <IconWrapper style={{ marginRight: '10px' }}>
                        {item.type === 'folder' && <FolderSvg height={22} width={22} />}
                        {item.type === 'template' && <Play height={22} width={22} />}
                      </IconWrapper>
                      {item.title}
                    </span>
                  </List.Item>
                )}
              />
            </>
          )}
        </p>
        <p>
          <T
            keyName="Lessons and folders in the trash folder will be permanently deleted after 30 days."
            defaultValue="Lessons and folders in the trash folder will be permanently deleted after 30 days."
          />
        </p>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            width: '100%',
          }}
        >
          <Button
            type="tertiary"
            styles={{ border: 'none', color: theme.colors.black, boxShadow: 'none' }}
            onClick={() => {
              setDeleteData(undefined);
            }}
          >
            {t('Cancel')}
          </Button>
          <Button
            onClick={() => handleDelete(deleteData)}
            width="100px"
            type="danger"
            styles={{ marginLeft: 10 }}
          >
            {isDeletingData && <LoadingOutlined />} {t('Delete')}
          </Button>
        </div>
      </BaseModal>
    </>
  );
};
